import { useEffect, useRef, useState } from 'react';
import {
  updateProjectMilestone,
  ApiError,
  type ProjectMilestoneStatusInput,
  type UpdateProjectMilestoneInput,
} from '../../lib/apiClient';
import type { ProjectMilestone } from '../../graphql/types';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';
import '@tylertech/forge/banner';
import './EditCatalogDialog.css';

export interface EditMilestoneDialogProps {
  /** Milestone being edited; `null` keeps the dialog closed. */
  milestone: ProjectMilestone | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const MILESTONE_STATUS_INPUTS: ProjectMilestoneStatusInput[] = [
  'not-started',
  'in-progress',
  'complete',
  'slipped',
];

/**
 * Normalize a milestone status read from GraphQL to the hyphenated REST input
 * form. GraphQL serializes the enum to its *external* names
 * (`not_started`/`in_progress`) while the REST write route's Zod schema expects
 * the hyphenated values (`not-started`/`in-progress`) — sending the underscore
 * form fails validation (same class of bug as issue #535). Accepting both forms
 * keeps the form robust regardless of which the read path returns.
 */
function toMilestoneStatusInput(status: string): ProjectMilestoneStatusInput {
  const normalized = status.replace(/_/g, '-');
  return (
    MILESTONE_STATUS_INPUTS.includes(normalized as ProjectMilestoneStatusInput)
      ? normalized
      : 'not-started'
  ) as ProjectMilestoneStatusInput;
}

export function EditMilestoneDialog({
  milestone,
  onSuccess,
  onCancel,
}: EditMilestoneDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);
  const [form, setForm] = useState<{
    name: string;
    sequence: string;
    status: ProjectMilestoneStatusInput;
    targetDate: string;
    description: string;
  }>({ name: '', sequence: '', status: 'not-started', targetDate: '', description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = milestone !== null;

  const DialogElement = 'forge-dialog' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.open = open;
    }
    if (milestone) {
      setForm({
        name: milestone.name,
        sequence: String(milestone.sequence),
        status: toMilestoneStatusInput(milestone.status),
        targetDate: milestone.targetDate ?? '',
        description: milestone.description ?? '',
      });
      setError(null);
    }
  }, [milestone, open]);

  const handleClose = (): void => {
    if (!isSubmitting) {
      onCancel();
    }
  };

  const canSubmit = form.name.trim() !== '' && !isSubmitting;

  const handleSubmit = async (): Promise<void> => {
    if (!milestone || !canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const input: UpdateProjectMilestoneInput = {
        name: form.name.trim(),
        sequence: Number(form.sequence) || milestone.sequence,
        status: form.status,
        targetDate: form.targetDate || undefined,
        description: form.description.trim() || undefined,
      };
      await updateProjectMilestone(milestone.entityId, input);
      onSuccess();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update milestone');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogElement
      ref={dialogRef}
      onForge-dialog-close={handleClose}
      className="edit-catalog-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-milestone-title"
    >
      <div className="dialog-content">
        <h2 id="edit-milestone-title" className="forge-typography--headline5 edit-catalog-title">
          Edit Milestone
        </h2>

        {error && (
          <BannerElement
            theme="error"
            dismissed={false}
            role="alert"
            className="edit-catalog-error"
          >
            <span>{error}</span>
          </BannerElement>
        )}

        <div className="edit-catalog-body">
          <div className="edit-catalog-field">
            <label className="forge-typography--caption edit-catalog-label" htmlFor="edit-ms-name">
              Name *
            </label>
            <input
              id="edit-ms-name"
              className="search-input"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Milestone name"
              aria-label="Milestone name"
              aria-required="true"
            />
          </div>

          <div className="edit-catalog-field">
            <label className="forge-typography--caption edit-catalog-label" htmlFor="edit-ms-seq">
              Sequence
            </label>
            <input
              id="edit-ms-seq"
              type="number"
              className="search-input"
              value={form.sequence}
              onChange={(e) => setForm((p) => ({ ...p, sequence: e.target.value }))}
              aria-label="Milestone sequence"
            />
          </div>

          <div className="edit-catalog-field">
            <label
              className="forge-typography--caption edit-catalog-label"
              htmlFor="edit-ms-status"
            >
              Status
            </label>
            <select
              id="edit-ms-status"
              className="filter-select"
              value={form.status}
              onChange={(e) =>
                setForm((p) => ({ ...p, status: e.target.value as ProjectMilestoneStatusInput }))
              }
              aria-label="Milestone status"
            >
              <option value="not-started">Not Started</option>
              <option value="in-progress">In Progress</option>
              <option value="complete">Complete</option>
              <option value="slipped">Slipped</option>
            </select>
          </div>

          <div className="edit-catalog-field">
            <label className="forge-typography--caption edit-catalog-label" htmlFor="edit-ms-date">
              Target Date
            </label>
            <input
              id="edit-ms-date"
              type="date"
              className="search-input"
              value={form.targetDate}
              onChange={(e) => setForm((p) => ({ ...p, targetDate: e.target.value }))}
              aria-label="Milestone target date"
            />
          </div>

          <div className="edit-catalog-field">
            <label className="forge-typography--caption edit-catalog-label" htmlFor="edit-ms-desc">
              Description
            </label>
            <input
              id="edit-ms-desc"
              className="search-input"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Optional description"
              aria-label="Milestone description"
            />
          </div>
        </div>

        <div className="dialog-actions">
          <ForgeButton type="button" variant="text" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </ForgeButton>
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {isSubmitting ? 'Saving…' : 'Save'}
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
